import React, { forwardRef } from "react";

import defaultStyles from "./main.module.scss";

type ChangeEvent = React.ChangeEvent<HTMLInputElement>;
type EventArgs = {
  name: ChangeEvent["target"]["name"];
  value: ChangeEvent["target"]["value"];
  checked: ChangeEvent["target"]["checked"];
  id: ChangeEvent["target"]["id"];
  _e: ChangeEvent;
};

const defaultOnChange = (e: EventArgs) => e;

export interface RadioInputProps extends Omit<React.ComponentPropsWithoutRef<"input">, "onChange"> {
  onChange: (options: EventArgs) => void | EventArgs;
  /**
   * Make sure to pass only Label instance from ui/src/components/form/Label
   */
  label?: React.ReactElement<any>;
  /**
   * These classes are appended to the parent container
   */
  classes?: string;
  /**
   * If the form field has error, pass this as true
   */
  error?: boolean;
  styles?: ModularStyles;
}

const RadioInput = forwardRef<HTMLInputElement, RadioInputProps>(
  (
    {
      placeholder = "",
      className = "",
      id = `input-${Date.now()}`,
      autoFocus = false,
      name = "radio-input",
      value = "",
      checked = false,
      disabled = false,
      error = false,
      classes = "",
      styles = defaultStyles,
      label,
      onChange = defaultOnChange,
      children,
      ...restProps
    },
    ref,
  ) => (
    <div
      className={`${styles.radioInput} ${classes} ${error && checked ? styles.formInputError : ""}`}
    >
      <input
        type="radio"
        placeholder={placeholder}
        className={className}
        name={name}
        id={id}
        ref={ref}
        disabled={disabled}
        autoFocus={autoFocus}
        value={value}
        checked={checked}
        onChange={(e) =>
          onChange({
            name: e.target.name,
            value: e.target.value,
            checked: e.target.checked,
            id: e.target.id,
            _e: e,
          })
        }
        {...restProps}
      />
      {label &&
        React.cloneElement(label, { checked, disabled, htmlFor: id, "aria-disabled": disabled })}
      {children}
    </div>
  ),
);

export default RadioInput;
